import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import BrandLogo from '../components/BrandLogo';
import Modal, { ModalCloseButton } from '../components/ui/Modal';
import {
  Trash2,
  AlertTriangle,
  Mail,
  CheckCircle2,
  Smartphone,
  Info,
  X,
} from 'lucide-react';

const DeleteAccount = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const SUPPORT_EMAIL = 'support@vitra.health.com';

  return (
    <div className="min-h-screen vitra-atmosphere flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <BrandLogo to="/" markClassName="h-10 w-10" wordmarkClassName="h-6" />
            <Link 
              to="/" 
              className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Trash2 className="w-8 h-8 text-red-600" strokeWidth={1.75} aria-hidden="true" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Delete Your Account & Data
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're sorry to see you go. Please read the information below carefully before requesting account deletion.
            </p>
          </div>

          {/* Info Cards */}
          <div className="space-y-6 mb-12">
            {/* What Gets Deleted */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" strokeWidth={2} aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">What Will Be Deleted</h2>
                  <p className="text-gray-600 mb-4">
                    When you request account deletion, the following data will be permanently removed:
                  </p>
                  <ul className="space-y-2">
                    {[
                      'Your personal profile information (name, email, phone number)',
                      'All health data and vital readings you have submitted',
                      'Medical history and clinical notes associated with your account',
                      'Appointment history and scheduling data',
                      'Message history and communication records',
                      'Medication tracking data and reminders',
                      'All files and documents uploaded to your account',
                      'App preferences and notification settings'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-600">
                        <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 rounded-2xl shadow-lg p-6 sm:p-8 border border-amber-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Info className="w-6 h-6 text-amber-600" strokeWidth={2} aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-amber-800 mb-3">Important Information</h2>
                  <ul className="space-y-3 text-amber-700">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                      <span><strong>This action is irreversible.</strong> Once your data is deleted, it cannot be recovered.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                      <span>Deletion requests are processed within <strong>30 days</strong> as per regulatory requirements.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                      <span>Some anonymized data may be retained for legal compliance and research purposes.</span>
                    </li>
                    {/* <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                      <span>You will receive a confirmation email once your data has been deleted.</span>
                    </li> */}
                  </ul>
                </div>
              </div>
            </div>

            {/* How to Request Deletion */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-teal-600" strokeWidth={2} aria-hidden="true" />
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">How to Request Account Deletion</h2>
                  <p className="text-gray-600 mb-6">
                    To delete your account and all associated data, please send an email to our support team with the following information:
                  </p>
                  
                  <div className="bg-gray-50 rounded-xl p-5 mb-6">
                    <h3 className="font-medium text-gray-900 mb-3">Include in your email:</h3>
                    <ol className="space-y-2 text-gray-600 list-decimal list-inside">
                      <li>Your registered email address</li>
                      <li>Your full name as registered in the app</li>
                      <li>Subject line: <span className="font-mono bg-gray-200 px-2 py-0.5 rounded text-sm">"Account Deletion Request"</span></li>
                      <li>A brief confirmation that you understand this action is permanent</li>
                    </ol>
                  </div>

                  {/* Contact Email Box */}
                  <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div>
                        <p className="text-teal-100 text-sm mb-1">Send your request to:</p>
                        <a 
                          href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}
                          className="text-2xl font-bold hover:text-teal-100 transition-colors break-all"
                        >
                          {SUPPORT_EMAIL}
                        </a>
                      </div>
                      <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request&body=Dear%20VITRA%20Support%20Team%2C%0A%0AI%20would%20like%20to%20request%20the%20deletion%20of%20my%20account%20and%20all%20associated%20data.%0A%0ARegistered%20Email%3A%20%0AFull%20Name%3A%20%0A%0AI%20understand%20that%20this%20action%20is%20permanent%20and%20cannot%20be%20undone.%0A%0AThank%20you.`}
                        className="flex-shrink-0 bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors shadow-lg flex items-center space-x-2"
                      >
                        <Mail className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                        <span>Send Email</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Alternative: Delete from App */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-purple-600" strokeWidth={2} aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Delete from Mobile App</h2>
                  <p className="text-gray-600 mb-4">
                    You can also request account deletion directly from the VITRA mobile app:
                  </p>
                  <ol className="space-y-3 text-gray-600">
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-semibold">1</span>
                      <span>Open the VITRA app and log in to your account</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-semibold">2</span>
                      <span>Go to <strong>Settings</strong> → <strong>Account</strong></span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-semibold">3</span>
                      <span>Tap on <strong>"Delete Account"</strong></span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-semibold">4</span>
                      <span>Follow the on-screen instructions to confirm deletion</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Need Help */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-lg p-6 sm:p-8 text-white">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Info className="w-6 h-6 text-white" strokeWidth={2} aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-3">Need Help?</h2>
                  <p className="text-gray-300 mb-4">
                    If you have any questions about the deletion process or would like to discuss alternatives, our support team is here to help.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="inline-flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
                    >
                      <Mail className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                      <span>{SUPPORT_EMAIL}</span>
                    </a>
                    <Link
                      to="/help"
                      className="inline-flex items-center space-x-2 bg-teal-500 hover:bg-teal-600 px-4 py-2 rounded-lg transition-colors"
                    >
                      <Info className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                      <span>Help Center</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Dialog */}
          <Modal
            open={showConfirmation}
            onClose={() => setShowConfirmation(false)}
          >
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100 relative">
                <ModalCloseButton
                  onClose={() => setShowConfirmation(false)}
                  className="absolute top-4 right-4"
                />
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={2} aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Client Opened</h3>
                  <p className="text-gray-600 mb-6">
                    Your email client should open with a pre-filled deletion request. If it doesn't, please send an email manually to <strong>{SUPPORT_EMAIL}</strong>
                  </p>
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Got it
                  </button>
                </div>
              </div>
          </Modal>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DeleteAccount;